import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { toast } from "sonner";
import {
  Package,
  Truck,
  CheckCircle2,
  Clock,
  ShoppingCart,
  ArrowUpRight,
  AlertTriangle,
  CalendarClock,
} from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, o] = await Promise.all([
          api.get("/api/dashboard/stats"),
          api.get("/api/orders"),
        ]);
        setStats(s.data);
        setRecent(o.data.slice(0, 6));
      } catch (e) {
        toast.error("Failed to load dashboard");
      }
    })();
  }, []);

  const StatCard = ({ label, value, icon: Icon, accent, testid }) => (
    <div
      className="card-brutal p-6 hoverable-card cursor-default"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
          {label}
        </div>
        <Icon
          className="h-4 w-4"
          style={{ color: accent || "var(--text-dim)" }}
          strokeWidth={2.5}
        />
      </div>
      <div
        className="font-display text-4xl mt-4"
        style={{ color: accent || "white" }}
      >
        {value}
      </div>
    </div>
  );

  if (!stats)
    return (
      <div className="p-6 sm:p-12 text-zinc-500 font-mono text-xs tracking-[0.3em]">
        LOADING...
      </div>
    );

  return (
    <div className="p-4 sm:p-6 lg:p-12 space-y-10">
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
            // COMMAND CENTER
          </div>
          <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl mt-3 leading-none">
            DASHBOARD
          </h1>
        </div>
        <button
          data-testid="quick-new-sale"
          onClick={() => nav("/pos")}
          className="btn-brutal bg-[color:var(--mecha-red)] text-white px-6 h-12 font-bold tracking-widest uppercase text-sm flex items-center gap-2"
        >
          <ShoppingCart className="h-4 w-4" />
          New Sale
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
        <StatCard
          testid="stat-today-revenue"
          label="TODAY REVENUE"
          value={fmtINR(stats.today_revenue)}
          icon={ArrowUpRight}
          accent="var(--success)"
        />
        <StatCard
          testid="stat-today-orders"
          label="TODAY ORDERS"
          value={stats.today_orders}
          icon={ShoppingCart}
        />
        <StatCard
          testid="stat-total-revenue"
          label="TOTAL REVENUE"
          value={fmtINR(stats.total_revenue)}
          icon={ArrowUpRight}
          accent="var(--mecha-red)"
        />
        <StatCard
          testid="stat-total-orders"
          label="TOTAL ORDERS"
          value={stats.total_orders}
          icon={Package}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800">
        <StatCard
          testid="stat-preorder"
          label="PRE-ORDERS"
          value={stats.preorder ?? 0}
          icon={CalendarClock}
          accent="#a78bfa"
        />
        <StatCard
          testid="stat-awaiting"
          label="AWAITING ADDRESS"
          value={stats.awaiting_address}
          icon={Clock}
          accent="var(--warn)"
        />
        <StatCard
          testid="stat-ready"
          label="READY TO PACK"
          value={stats.ready_to_ship}
          icon={Package}
          accent="var(--neon-cyan)"
        />
        <StatCard
          testid="stat-packed"
          label="PACKED"
          value={stats.packed ?? 0}
          icon={Package}
          accent="#f59e0b"
        />
        <StatCard
          testid="stat-shipped"
          label="SHIPPED"
          value={stats.shipped}
          icon={Truck}
          accent="var(--success)"
        />
      </div>

      {stats.low_stock > 0 && (
        <div
          data-testid="low-stock-warning"
          className="border border-[color:var(--warn)] bg-[color:var(--warn)]/5 p-5 flex items-center gap-4"
        >
          <AlertTriangle className="h-5 w-5 text-[color:var(--warn)]" />
          <div className="text-sm">
            <span className="font-mono text-[color:var(--warn)]">
              {stats.low_stock}
            </span>{" "}
            product(s) running low on stock.{" "}
            <button
              onClick={() => nav("/products")}
              className="underline hover:text-[color:var(--warn)]"
            >
              Review inventory →
            </button>
          </div>
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl">RECENT ORDERS</h2>
          <button
            onClick={() => nav("/orders")}
            className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 hover:text-white"
          >
            VIEW ALL →
          </button>
        </div>
        <div className="card-brutal overflow-x-auto">
          {recent.length === 0 ? (
            <div className="p-12 text-center text-zinc-500 text-sm">
              No orders yet. Head to the POS to make your first sale.
            </div>
          ) : (
            <table className="w-full min-w-[560px]">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left font-mono text-[10px] tracking-[0.3em] text-zinc-500 p-4">
                    ORDER
                  </th>
                  <th className="text-left font-mono text-[10px] tracking-[0.3em] text-zinc-500 p-4">
                    CUSTOMER
                  </th>
                  <th className="text-left font-mono text-[10px] tracking-[0.3em] text-zinc-500 p-4">
                    STATUS
                  </th>
                  <th className="text-right font-mono text-[10px] tracking-[0.3em] text-zinc-500 p-4">
                    TOTAL
                  </th>
                </tr>
              </thead>
              <tbody>
                {recent.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => nav(`/orders/${o.id}`)}
                    className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/50 cursor-pointer"
                    data-testid={`recent-order-${o.order_number}`}
                  >
                    <td className="p-4 font-mono text-sm">{o.order_number}</td>
                    <td className="p-4 text-sm text-zinc-300">
                      {o.customer_snapshot?.name || "—"}
                    </td>
                    <td className="p-4">
                      <StatusBadge status={o.status} />
                    </td>
                    <td className="p-4 text-right font-mono font-bold">
                      {fmtINR(o.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    awaiting_address: { bg: "var(--warn)", label: "AWAITING ADDR" },
    address_received: { bg: "var(--neon-cyan)", label: "READY" },
    packed: { bg: "#f59e0b", label: "PACKED" },
    shipped: { bg: "var(--success)", label: "SHIPPED" },
    delivered: { bg: "#a1a1aa", label: "DELIVERED" },
    cancelled: { bg: "var(--mecha-red)", label: "CANCELLED" },
    preorder: { bg: "#a78bfa", label: "PRE-ORDER" },
  };
  const s = map[status] || { bg: "#71717a", label: status?.toUpperCase() };
  return (
    <span
      className="font-mono text-[10px] tracking-[0.2em] px-2 py-1 text-black font-bold"
      style={{ background: s.bg }}
    >
      {s.label}
    </span>
  );
}
